<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">BudgetPro - Settings</h1>
    <span class="banner py-3 mb-4">
      <div>You will be able to locate your settings on the left-side navigation pane. The set of tabs provided will allow you to set or change how your budget works.</div>
      <div>Expand any of the topics below for more information.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>General Settings</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This is where the settings for each version are located. Some of the settings may not be available to change, depending on the status of the budget.</div>
          <ul>
            <li><strong>Version Description</strong> - This field can be edited at any time. It will help you differentiate your various versions. You may want to start by identifying it as Version 1.</li>
            <li><strong>Template</strong> - The template that ResMan creates for you will default, but you can select any template that you may have created. The purpose for a template is to determine which GL Accounts will need a budget and present them in a logical fashion.</li>
            <li class="no-bullet">
              <ul>
                <li>ResMan recommends that you use the ResMan Default Budget Template as it will be organized to match your chart of accounts.</li>
              </ul>
            </li>
            <li><strong>Estimate Future Historicals</strong> - When working on your budget, you may wish to view your Historical data.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  For Example: If you are working on your 2022 budget you would want to see a full year of historical data for 2021. However, if it is August of 2021 when you are working on your budget, there are months that have do not exist: August, September, October, November and December. Therefore we provide three options to fill in the missing months:
                </li>
                <li class="no-bullet">
                  <ul>
                    <li>
                      <strong>1. Estimate Future Historicals by annualizing current year (2021) actuals and start in _____ Month</strong>
                    </li>
                    <li class="no-bullet">
                      <ul>
                        <li>This will take the prior current month actuals and average them and apply that as "historical" numbers starting in the month selected.</li>
                        <li>For Example, if you select this option and select August, the January through July actual numbers will be averaged. That average will be populated in August through December.</li>
                      </ul>
                    </li>
                    <li>2. Estimate Future Historicals by using prior year (2020) actuals and start in _____ Month</li>
                    <li class="no-bullet">
                      <ul>
                        <li>For the month selected through the ending month of the budget, actual numbers from the prior year will be displayed.</li>
                        <li>For Example, if you are working on a 2022 budget, and you select this option and select August, the August through December numbers will be populated with actual historical numbers from 2020.</li>
                      </ul>
                    </li>
                    <li>3. Estimate Future Historicals using current year (2021) budget and start in _____ Month</li>
                    <li class="no-bullet">
                      <ul>
                        <li>For the month selected through the ending month of the budget, budgeted numbers from the current budget year will be displayed.</li>
                        <li>For Example: If you select this option and select August, then August through December will have your current year budget numbers displayed.</li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>Approval Workflow</strong> - Initially no approval workflow will be selected. If you have created an approval workflow within Admin > Settings > Workflows for a budget, you will be able to select it here.</li>
            <li class="no-bullet">
              <ul>
                <li>Note: You will be able to change the Approval Workflow until the budget is Submitted for Approval or Approved. Then this setting will be locked.</li>
                <li>Note: Even if you do not select an Approval Workflow, your budget will still need to be Approved before it can be Published.</li>
              </ul>
            </li>
          </ul>
          <div>The settings below were set when the budget was created and can not be changed. If needed, you can Archive this budget and create a new one if you made an error in your choices.</div>
          <ul>
            <li><strong>Property</strong> - Select the property that you would like to budget</li>
            <li><strong>Start Year</strong> - Select the Year that this budget should begin</li>
            <li><strong>Start Month</strong> - Select the Month that this budget should begin. The first month of your accounting year will be provided for you, but you can change this month if desired.</li>
            <li class="no-bullet">
              <ul>
                <li>Note: Your budget is identified by the last month of the budget. If your budget runs from July 2021 - June 2022 then it will be referred to as the 2022 budget on future screens.</li>
              </ul>
            </li>
            <li><strong>Accounting Basis</strong> - Select whether you would like to budget for Cash basis or Accrual Basis. You may create a separate budget for each one if desired.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Lease Calculations</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This tab is going to allow you to determine how your new leases and your renewal leases are priced.</div>
          <ul>
            <li><strong>NEW LEASES</strong> - You will have three choices regarding how you price your new leases.</li>
            <li class="no-bullet">
              <ul>
                <li><strong>1. New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this option is selected a new line will appear "The discount will be ___% of Market Rent."</li>
                    <li>If this is selected, the percentage entered will populate on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
                  </ul>
                </li>
                <li><strong>2. New Leases will be at Market Rent (no Gain/Loss to Lease)</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this is selected, "MKT" will display on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
                  </ul>
                </li>
                <li><strong>3. New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent".</li>
                    <li>You will select a single concession Transaction Category.</li>
                    <li>If this is selected, "MKT" will display on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
                    <li>Also, if this is selected, the Occupancy > Rental Deductions tab > Recurring Move In Concessions line will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. This is editable.</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>RENEWAL LEASES</strong> - You will have three choices regarding how you price your renewals.</li>
            <li class="no-bullet">
              <ul>
                <li><strong>1. Renewals will be given an increase over existing rent (Default Choice)</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this option is selected a new line will appear "This increase will be ____Dollars/Percent over existing rent."</li>
                    <li>If percentage is selected, the percentage entered will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line.</li>
                    <li>If dollar is selected, it will prefill on the Occupancy > Actual Rent tab > Renewal Increase $ line.</li>
                  </ul>
                </li>
                <li><strong>2. Renewals will be at Market Rent</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this option is selected, "MKT" will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li>
                  </ul>
                </li>
                <li><strong>3. Renewals will be at Market Rent AND also offer a recurring concession</strong></li>
                <li class="no-bullet">
                  <ul>
                    <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent and will use concession transaction category:"</li>
                    <li>The user will select a single Transaction Category</li>
                    <li>If this is selected, the Occupancy > Rental Deductions tab > Recurring Renewal Concessions will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. This is editable.</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Concessions & Bad Debt</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This tab is going to allow you to select concessions and bad debt transaction categories.</div>
          <div><strong>Other Concessions</strong> - Any Transaction Categories selected on this drop-down will appear on the Occupancy > Rental Deductions > Other Concession</div>
          <div><strong>Bad Debt</strong> - You have three options for calculating your bad debt write offs. If you choose one of the calculation methods (the first two options in the drop-down), the resulting numbers will appear in the Occupancy > Rental Deductions tab > Bad Debt line item. If you select Manual Entry, nothing will populate.</div>
          <ul>
            <li><strong>1. Bad Debt will be a percentage of the selected GL Account</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected a new line will appear: "This will be _____% of ________________________________"</li>
                <li class="no-bullet">
                  <ul>
                    <li>1st question: What is the percentage?</li>
                    <li>2nd question: What is the GL Account?</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li><strong>2. Bad Debt will be Average Market Rent multiplied by the number of Skips/Evictions.</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear.</li>
              </ul>
            </li>
            <li><strong>3. Manual Entry</strong></li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear.</li>
              </ul>
            </li>
            <li><strong>Bad Debt Transaction Category</strong> - No matter which of the three choices were selected, there will be a final drop-down where you can select your Transaction Category for Bad Debt Write Off. Your Transaction Category description will appear on the Occupancy > Rental Deductions > Bad Debt line description.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Beginning Vacancies</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This tab is going to allow you to determine your starting Vacant Unit count.</div>
          <div>Initially it will display zeroes for each unit type. You can manually select a number for each unit type, or you can click <strong>LOAD PROJECTED VACANCIES AS OF 12/31/21</strong>. The date will be the day before your budget starts.</div>
          <ul>
            <li>If you click this option, it will take your vacancies as of "today" and add any preleases and subtract any Notice to Vacates that currently exist.</li>
            <li>You can select this option again later to update these vacancies later in the year.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Edit Budgets')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'EditBudgetsHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
    order: 0;
  }

  .header {
    order: 1;
  }

  .help-container {
    border-bottom: 1px solid var(--v-resGrey-lighten1)
  }

  .help-content div,
  .help-content li {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .pointer {
    cursor: pointer !important;
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }

    .separator::before,
    .separator::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid var(--v-resGrey-darken1);
    }

    .separator:not(:empty)::before {
      margin-right: .25em;
    }

    .separator:not(:empty)::after {
      margin-left: .25em;
    }
</style>
